/**
 * Customer Portal API service.
 * Separate axios instance with different localStorage keys so staff and customer sessions never collide.
 */

import axios, { AxiosInstance } from 'axios';
import { safeStorage } from '../utils/safeStorage';

// ── Types ────────────────────────────────────────────────────────────

export interface OTPRequestResponse {
  otp_sent: boolean;
  delivery_method: 'sms' | 'email';
  message: string;
}

export interface CustomerTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface CustomerProfile {
  id: string;
  first_name: string;
  last_name: string;
  phone?: string;
  email?: string;
}

export interface AccountSummary {
  account_id: string;
  account_status: string;
  buyer_name: string;
  vehicle_year_make_model: string;
  total_balance_amount: number;
  payment_amount: number;
  payment_due_date?: string;
  days_past_due: number;
  last_payment_date?: string;
  // Phase A5:
  id: string;
  promise_date?: string | null;
  balance_late_amount: number;
  remaining_payment: number;
  payment_count: number;
}

export interface PaymentHistoryItem {
  id: string;
  paid_date?: string;
  paid_amount?: number;
  payment_method?: string;
  payment_type?: string;
  principal_applied_amt?: number;
  interest_applied_amt?: number;
  late_fees_applied_amt?: number;
}

export interface ScheduleRow {
  period_number: number;
  due_date: string;
  payment: number;
  principal: number;
  interest: number;
  status: 'paid' | 'partial' | 'unpaid';
}

export interface ScheduleResponse {
  account_id: string;
  has_schedule: boolean;
  status_basis: string;
  has_unallocated_payments: boolean;
  schedule: ScheduleRow[];
}

export interface PayoffQuoteResponse {
  account_id: string;
  as_of_date: string;
  current_total_balance: number;
  per_diem?: number | null;
  per_diem_days: number;
  estimated_payoff: number;
  per_diem_available: boolean;
  disclaimer: string;
}

export type PortalRequestType = 'contact' | 'dispute' | 'promise_to_pay';
export type PortalRequestStatus = 'open' | 'in_review' | 'resolved' | 'closed';
export type PortalMessageAuthorType = 'borrower' | 'staff';

export interface CreatePortalRequestPayload {
  request_type: PortalRequestType;
  subject: string;
  message: string;
  account_id?: string;
  payment_id?: string;
  proposed_promise_date?: string;
}

export interface PortalMessage {
  id: string;
  author_type: PortalMessageAuthorType;
  body: string;
  created_at: string;
  is_backfilled: boolean;
}

export interface PortalRequest {
  id: string;
  request_type: PortalRequestType;
  status: PortalRequestStatus;
  subject: string;
  message: string;
  account_id?: string | null;
  payment_id?: string | null;
  proposed_promise_date?: string | null;
  staff_response?: string | null;
  created_at: string;
  // v2 servicing (Slice 3/6): thread cache + embedded recent messages.
  // Optional so legacy-shaped fixtures/mocks stay valid during the drain window.
  last_message_at?: string | null;
  last_message_author?: PortalMessageAuthorType | null;
  last_message_id?: string | null;
  borrower_last_viewed_message_id?: string | null;
  unread?: boolean;
  messages?: PortalMessage[];
  archived_by_borrower_at?: string | null;
}

// ── Storage keys (separate from staff auth) ──────────────────────────

const CUSTOMER_ACCESS_TOKEN_KEY = 'customer_access_token';
const CUSTOMER_REFRESH_TOKEN_KEY = 'customer_refresh_token';

// v2 servicing drain telemetry (A-1 clause 2): old bundles send no such
// header — its absence is the drain discriminator, so this must be set
// unconditionally on every request, never omitted for a missing build var.
const PORTAL_CLIENT_VERSION = process.env.REACT_APP_GIT_SHA || 'dev';

// ── Service ──────────────────────────────────────────────────────────

class CustomerPortalApiService {
  private client: AxiosInstance;

  constructor() {
    const envUrl = process.env.REACT_APP_API_URL;
    const baseURL = envUrl === '' ? '' : envUrl || 'http://localhost:8000';

    this.client = axios.create({
      baseURL: `${baseURL}/api/v1/customer-portal`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-Portal-Client-Version': PORTAL_CLIENT_VERSION,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Attach customer token to requests
    this.client.interceptors.request.use((config) => {
      const token = safeStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Auto-refresh on 401
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url = originalRequest?.url || '';

        // Don't retry login/refresh endpoints
        if (url.includes('/login/') || url.includes('/refresh')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const storedRefresh = safeStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY);
          if (!storedRefresh) {
            return Promise.reject(error);
          }

          try {
            const tokens = await this.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
            return this.client(originalRequest);
          } catch {
            this.clearTokens();
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private setTokens(tokens: CustomerTokenResponse) {
    safeStorage.setItem(CUSTOMER_ACCESS_TOKEN_KEY, tokens.access_token);
    safeStorage.setItem(CUSTOMER_REFRESH_TOKEN_KEY, tokens.refresh_token);
  }

  private clearTokens() {
    safeStorage.removeItem(CUSTOMER_ACCESS_TOKEN_KEY);
    safeStorage.removeItem(CUSTOMER_REFRESH_TOKEN_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('customer-auth:token-cleared'));
    }
  }

  // ── Auth Methods (OTP-based) ───────────────────────────────────────

  async requestOTP(identifier: string, ssnOrAccount: string): Promise<OTPRequestResponse> {
    const response = await this.client.post<OTPRequestResponse>('/login/request-otp', {
      identifier,
      ssn_or_account: ssnOrAccount,
    });
    return response.data;
  }

  async verifyOTP(identifier: string, code: string): Promise<CustomerTokenResponse> {
    const response = await this.client.post<CustomerTokenResponse>('/login/verify-otp', {
      identifier,
      code,
    });
    this.setTokens(response.data);
    return response.data;
  }

  async refreshToken(): Promise<CustomerTokenResponse> {
    const refreshToken = safeStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('No refresh token');

    const response = await this.client.post<CustomerTokenResponse>('/refresh', {
      refresh_token: refreshToken,
    });
    this.setTokens(response.data);
    return response.data;
  }

  logout() {
    this.clearTokens();
  }

  // ── Data Methods ─────────────────────────────────────────────────

  async getMe(): Promise<CustomerProfile> {
    const response = await this.client.get<CustomerProfile>('/me');
    return response.data;
  }

  async getAccount(): Promise<AccountSummary[]> {
    const response = await this.client.get<AccountSummary[]>('/account');
    return response.data;
  }

  async getPayments(skip = 0, limit = 50): Promise<PaymentHistoryItem[]> {
    const response = await this.client.get<PaymentHistoryItem[]>('/payments', {
      params: { skip, limit },
    });
    return response.data;
  }

  async getSchedule(accountId: string): Promise<ScheduleResponse> {
    const response = await this.client.get<ScheduleResponse>(`/accounts/${accountId}/schedule`);
    return response.data;
  }

  async getPayoffQuote(accountId: string, asOfDate?: string): Promise<PayoffQuoteResponse> {
    const response = await this.client.get<PayoffQuoteResponse>(
      `/accounts/${accountId}/payoff-quote`,
      {
        params: asOfDate ? { as_of_date: asOfDate } : {},
      }
    );
    return response.data;
  }

  async downloadReceiptPdf(paymentId: string): Promise<Blob> {
    const response = await this.client.get(`/payments/${paymentId}/receipt`, {
      params: { format: 'pdf' },
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async downloadStatementPdf(accountId: string, year: number, month: number): Promise<Blob> {
    const response = await this.client.get(`/accounts/${accountId}/statement`, {
      params: { year, month, format: 'pdf' },
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async createRequest(payload: CreatePortalRequestPayload): Promise<PortalRequest> {
    const response = await this.client.post<PortalRequest>('/requests', payload);
    return response.data;
  }

  async getRequests(includeArchived = false): Promise<PortalRequest[]> {
    const response = await this.client.get<PortalRequest[]>('/requests', {
      params: includeArchived ? { include_archived: true } : {},
    });
    return response.data;
  }

  async getRequestMessages(
    requestId: string,
    before?: string,
    limit = 50
  ): Promise<PortalMessage[]> {
    const response = await this.client.get<PortalMessage[]>(`/requests/${requestId}/messages`, {
      params: { before, limit },
    });
    return response.data;
  }

  async postRequestMessage(
    requestId: string,
    body: string,
    clientMessageId: string
  ): Promise<PortalMessage> {
    const response = await this.client.post<PortalMessage>(`/requests/${requestId}/messages`, {
      body,
      client_message_id: clientMessageId,
    });
    return response.data;
  }

  async archiveRequest(requestId: string): Promise<PortalRequest> {
    const response = await this.client.post<PortalRequest>(`/requests/${requestId}/archive`);
    return response.data;
  }

  async unarchiveRequest(requestId: string): Promise<PortalRequest> {
    const response = await this.client.post<PortalRequest>(`/requests/${requestId}/unarchive`);
    return response.data;
  }

  async markViewed(requestId: string, lastSeenMessageId: string): Promise<PortalRequest> {
    const response = await this.client.post<PortalRequest>(`/requests/${requestId}/viewed`, {
      last_seen_message_id: lastSeenMessageId,
    });
    return response.data;
  }

  // ── Utility ──────────────────────────────────────────────────────

  isAuthenticated(): boolean {
    return !!safeStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);
  }
}

export const customerPortalApi = new CustomerPortalApiService();
export default customerPortalApi;

/** Trigger a browser download for an authed blob (keeps the JWT on the XHR — never window.open an authed URL). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
